/**
 * WYSIWYG Setup Module
 * Initializes WYSIWYG editor with dependency injection.
 */

import { WysiwygRender } from "./wysiwyg-render";
import { WysiwygHandler } from "./wysiwyg-handler";
import { WysiwygListener } from "./wysiwyg-listener";
import { WysiwygForm } from "./wysiwyg-form";
import { WysiwygUtils } from "./wysiwyg-utils";
import { WysiwygMentionSetup } from "./wysiwyg-mention/wysiwyg-mention-setup";
import { WysiwygImageToolbarSetup } from "./wysiwyg-image-toolbar/wysiwyg-image-toolbar-setup";
import { WysiwygProductEmbedSetup } from "./wysiwyg-product-embed/wysiwyg-product-embed-setup";
import { WysiwygProductEmbedBlotUtils } from "./wysiwyg-product-embed/wysiwyg-product-embed-blot-utils";
import { WysiwygUploadPlaceholderBlotUtils } from "./wysiwyg-upload-placeholder-blot-utils";
import { WysiwygImageFormatUtils } from "./wysiwyg-image-format-utils";
import { phpAuthUser, phpQuill } from "../core/php-data-access";
import { Roles } from "../constants/roles";
import { translator } from "../translation/translator";
import { Patterns } from "../constants/patterns";
interface DeltaLike {
    insert(text: string): DeltaLike;
}

type ClipboardMatcher = (node: Element, delta: DeltaLike) => DeltaLike;

type QuillConfigValue = unknown;

interface QuillImportLike {
    import?(name: string): (new () => DeltaLike) | null | undefined;
}

interface ToolbarModuleLike {
    container?: Element | null;
}

interface QuillLike {
    root: HTMLElement;
    clipboard: {
        convert(html: string): DeltaLike;
    };
    getModule(name: string): ToolbarModuleLike | null | undefined;
    getLength(): number;
    setContents(delta: DeltaLike, source?: string): void;
    setSelection(index: number, length: number, source?: string): void;
    format(name: string, value: QuillConfigValue, source?: string): void;
}

interface WysiwygEditorHost extends HTMLElement {
    __quill?: QuillLike;
    __wysiwygHandler?: WysiwygHandler;
    __wysiwygRender?: WysiwygRender;
}

interface QuillConfig {
    theme: string;
    placeholder: string;
    modules: {
        toolbar: QuillConfigValue[];
        clipboard: {
            matchVisual: boolean;
            matchers: Array<[string, ClipboardMatcher]>;
        };
    };
}

interface QuillConstructorLike extends QuillImportLike {
    new (element: Element, config: QuillConfig): QuillLike;
}

interface WysiwygInitResult {
    quill: QuillLike;
    render: WysiwygRender;
    handler: WysiwygHandler;
    editorDiv: HTMLElement;
}

class WysiwygSetup {
    public static getDefaultConfig(): QuillConfig {
        return {
            theme: "snow",
            placeholder: translator?.getTranslation("wysiwyg_content_placeholder") || "เขียนเนื้อหาที่นี่...",
            modules: {
                toolbar: [
                    [{ header: [1, 2, 3, 4, false] }],
                    ["bold", "italic", "underline"],
                    [{ align: [] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link", "image", "video"],
                    [{ color: [] }, { background: [] }],
                    ["clean"],
                ],
                clipboard: {
                    matchVisual: false,
                    matchers: [
                        [
                            "iframe",
                            (node: Element, delta: DeltaLike) => {
                                const Quill = phpQuill() as unknown as QuillConstructorLike | null;
                                const Delta = Quill?.import ? Quill.import("delta") : null;
                                if (!Delta) return delta;
                                const src = node.getAttribute("src");

                                if (src) {
                                    const fbMatch = src.match(Patterns.IFRAME_HREF_PARAM);
                                    if (fbMatch) {
                                        const url = decodeURIComponent(fbMatch[1]);
                                        return new Delta().insert(url + "\n");
                                    }

                                    const ytMatch = src.match(Patterns.YOUTUBE_EMBED_SRC);
                                    if (ytMatch) {
                                        const videoId = ytMatch[1];
                                        const url = `https://www.youtube.com/watch?v=${videoId}`;
                                        return new Delta().insert(url + "\n");
                                    }

                                    const igMatch = src.match(Patterns.INSTAGRAM_EMBED_SRC);
                                    if (igMatch) {
                                        return new Delta();
                                    }
                                }

                                console.warn("iframe tag detected and removed for security");
                                return new Delta();
                            },
                        ],
                        [
                            "blockquote",
                            (node: Element, delta: DeltaLike) => {
                                const Quill = phpQuill() as unknown as QuillConstructorLike | null;
                                const Delta = Quill?.import ? Quill.import("delta") : null;
                                if (!Delta) return delta;

                                const className = (node.getAttribute("class") || "").toLowerCase();
                                if (!className.includes("instagram-media")) return delta;

                                const permalinkRaw = (node.getAttribute("data-instgrm-permalink") || "").trim();
                                if (permalinkRaw) {
                                    const url = Patterns.decodeAmpersand(permalinkRaw);
                                    return new Delta().insert(url + "\n");
                                }

                                const anchor = WysiwygForm.getInstagramAnchor(node);
                                if (anchor instanceof HTMLAnchorElement) {
                                    const href = (anchor.getAttribute("href") || "").trim();
                                    if (href) {
                                        const url = Patterns.decodeAmpersand(href);
                                        return new Delta().insert(url + "\n");
                                    }
                                }

                                return delta;
                            },
                        ],
                        [
                            "script",
                            (_node: Element, delta: DeltaLike) => {
                                const Quill = phpQuill() as unknown as QuillConstructorLike | null;
                                const Delta = Quill?.import ? Quill.import("delta") : null;
                                if (!Delta) return delta;
                                console.warn("script tag detected and removed for security");
                                return new Delta();
                            },
                        ],
                    ],
                },
            },
        };
    }

    public static init(
        textarea: HTMLTextAreaElement | null,
        config: Partial<QuillConfig> = {},
    ): WysiwygInitResult | null {
        if (!textarea) {
            console.error("Textarea element is required");
            return null;
        }

        const Quill = phpQuill() as unknown as QuillConstructorLike | null;
        if (typeof Quill === "undefined" || Quill === null) {
            console.warn("Quill library not loaded, falling back to plain textarea");
            WysiwygRender.enableTextareaFallback(textarea);
            return null;
        }

        const editorDiv = WysiwygRender.mountEditorForTextarea(textarea) as WysiwygEditorHost | null;
        if (!editorDiv) {
            console.warn("Failed to mount WYSIWYG editor container");
            WysiwygRender.enableTextareaFallback(textarea);
            return null;
        }

        if (
            textarea.dataset.wysiwygInitialized === "1" &&
            editorDiv.__quill &&
            editorDiv.__wysiwygHandler &&
            editorDiv.__wysiwygRender
        ) {
            return {
                quill: editorDiv.__quill,
                render: editorDiv.__wysiwygRender,
                handler: editorDiv.__wysiwygHandler,
                editorDiv,
            };
        }

        WysiwygImageFormatUtils.register(Quill as unknown as Parameters<typeof WysiwygImageFormatUtils.register>[0]);
        WysiwygProductEmbedBlotUtils.register(
            Quill as unknown as Parameters<typeof WysiwygProductEmbedBlotUtils.register>[0],
        );
        WysiwygUploadPlaceholderBlotUtils.register(
            Quill as unknown as Parameters<typeof WysiwygUploadPlaceholderBlotUtils.register>[0],
        );

        const mergedConfig = { ...this.getDefaultConfig(), ...config } as QuillConfig;
        if (textarea.dataset.videoEnabled === "0") {
            mergedConfig.modules.toolbar = mergedConfig.modules.toolbar
                .map((group) => (Array.isArray(group) ? group.filter((item) => item !== "video") : group))
                .filter((group) => !Array.isArray(group) || group.length > 0);
        }
        const quill = new Quill(editorDiv, mergedConfig);
        const toolbarModule = quill.getModule("toolbar");
        if (toolbarModule?.container instanceof HTMLElement) {
            toolbarModule.container.dataset.role = Roles.WYSIWYG_TOOLBAR;
            toolbarModule.container.setAttribute("tabindex", "-1");
            WysiwygForm.getAllDescendantElements(toolbarModule.container).forEach((el) =>
                el.setAttribute("tabindex", "-1"),
            );

            // Move toolbar before editorDiv so position:sticky works relative to the modal scroll
            // container. overflow:auto on .wysiwyg-editor (required for resize) would otherwise
            // trap the sticky element inside that container instead of the modal body.
            if (editorDiv.parentElement) {
                toolbarModule.container.classList.add("ql-toolbar--detached");
                editorDiv.parentElement.insertBefore(toolbarModule.container, editorDiv);
            }
        }
        if (quill.root instanceof HTMLElement) {
            quill.root.dataset.role = Roles.WYSIWYG_CONTENT;
        }

        const applyLocalizedPlaceholder = () => {
            const placeholder = translator?.getTranslation("wysiwyg_content_placeholder") || "เขียนเนื้อหาที่นี่...";
            if (quill.root instanceof HTMLElement) {
                quill.root.setAttribute("data-placeholder", placeholder);
            }
        };
        applyLocalizedPlaceholder();

        WysiwygListener.bindLocalizedPlaceholder(editorDiv, applyLocalizedPlaceholder);

        editorDiv.classList.add("quill-initialized");

        const boardId = textarea.dataset.boardId || "default";
        const authUserId = phpAuthUser()?.user_id;
        const userId = typeof authUserId === "number" ? authUserId : Number(authUserId) || 0;
        const render = new WysiwygRender(boardId, userId);

        const handler = new WysiwygHandler(
            quill as unknown as ConstructorParameters<typeof WysiwygHandler>[0],
            render,
            textarea,
        );

        editorDiv.__quill = quill;
        editorDiv.__wysiwygHandler = handler;
        editorDiv.__wysiwygRender = render;
        textarea.dataset.wysiwygInitialized = "1";

        WysiwygListener.setup(quill as unknown as Parameters<typeof WysiwygListener.setup>[0], handler, textarea);
        WysiwygMentionSetup.init(
            textarea,
            quill as unknown as Parameters<typeof WysiwygMentionSetup.init>[1],
            editorDiv,
        );
        WysiwygImageToolbarSetup.init(
            quill as unknown as Parameters<typeof WysiwygImageToolbarSetup.init>[0],
            editorDiv,
            textarea,
        );
        WysiwygProductEmbedSetup.init(
            quill as unknown as Parameters<typeof WysiwygProductEmbedSetup.init>[0],
            editorDiv,
            textarea,
        );

        if (textarea.value.trim()) {
            try {
                const delta = quill.clipboard.convert(textarea.value);
                quill.setContents(delta, "api");
                WysiwygUtils.dispatchTextareaEvent(textarea, "input");

                WysiwygUtils.extractAndStoreSNSLinks(textarea.value, textarea);
            } catch (error) {
                console.error("Error setting initial content:", error);
            }
        } else {
            try {
                quill.format("header", false, "silent");
            } catch (_error) {
                // ignore
            }

            try {
                const toolbarContainer = quill.getModule("toolbar")?.container;
                if (toolbarContainer instanceof HTMLElement) {
                    const headerSelect = WysiwygForm.getToolbarHeaderSelect(toolbarContainer);
                    if (headerSelect instanceof HTMLSelectElement) {
                        headerSelect.value = "";
                    }
                }
            } catch (_error) {
                // ignore
            }
        }

        render.extractExistingContentImages(quill.root);
        render.updateContentImagesList();

        return { quill, render, handler, editorDiv };
    }
}

export { WysiwygSetup };
